/**
 * «Обычно в месяц» — по истории трат, а не по одной оценке.
 *
 * Оценку человек даёт один раз, при знакомстве, и дальше она стареет: у
 * владельца свободные траты записаны 15 000 ₽, а уходит 38 000. Поэтому
 * «обычно» — серединный месяц из последних полных месяцев учёта, до шести.
 * Середина, а не среднее: один месяц с ремонтом или крупной покупкой не
 * задирает «обычно» на полгода вперёд.
 *
 * Оценка человека — стартовая точка: она стоит в ряду как ещё один месяц.
 * Так первые месяцы цифра движется плавно, а через полгода её почти целиком
 * задаёт история. Закреплённую человеком оценку история не трогает.
 *
 * Жизнь — без платежей: оплата обязательств, взносы в цели и платежи по
 * кредитам считаются отдельно, и в «обычно» они вошли бы второй раз. То же —
 * с платежом, записанным обычным расходом (`isPayment`).
 */
import { ZERO, type Money } from './money.js';
import { isKnown, type Amount } from './amount.js';
import type { PlainDate } from './date.js';
import { monthOf, monthReport, shiftMonth, type AnalyticsTx } from './analytics.js';

/** Сколько последних полных месяцев смотрим. */
export const USUAL_MONTHS = 6;
/**
 * Сколько трат должно быть записано за месяц, чтобы он считался. Месяц без
 * записей — это «не записывал», а не «ничего не потратил»: засчитай его нулём,
 * и «обычно» упало бы у каждого, кто пропустил месяц.
 */
export const USUAL_MIN_RECORDS = 5;

export interface UsualPart {
  value: Money;
  known: boolean;
  /**
   * Откуда цифра: `history` — по месяцам учёта (вместе с оценкой), `estimate` —
   * полных месяцев ещё нет, `pinned` — человек закрепил своё число, `none` — ни
   * истории, ни оценки.
   */
  source: 'history' | 'estimate' | 'pinned' | 'none';
  /** Сколько полных месяцев вошло. */
  months: number;
}

export interface Usual {
  need: UsualPart;
  want: UsualPart;
  /** Полные месяцы с записями, по которым считали, по возрастанию: «2026-09». */
  months: string[];
}

export interface UsualInput<T extends AnalyticsTx> {
  txs: readonly T[];
  today: PlainDate;
  /** С какого дня ведётся учёт: месяц, начатый посреди, неполный. */
  trackingStartedAt?: PlainDate;
  estimate: { need: Amount; want: Amount };
  pinned?: { need?: boolean; want?: boolean };
  /** Обычный расход, который на деле оплата платежа. */
  isPayment?: (t: T) => boolean;
}

/** Последние полные месяцы учёта, до `max`: без начатого посреди и без текущего. */
export function fullMonths(today: PlainDate, trackingStartedAt: PlainDate | undefined, max = USUAL_MONTHS): string[] {
  if (!trackingStartedAt) return [];
  const first = trackingStartedAt.endsWith('-01') ? monthOf(trackingStartedAt) : shiftMonth(monthOf(trackingStartedAt), 1);
  const out: string[] = [];
  for (let m = shiftMonth(monthOf(today), -1); m >= first && out.length < max; m = shiftMonth(m, -1)) out.push(m);
  return out.reverse();
}

/** Середина ряда: при чётном числе — среднее двух серединных. */
export function middle(xs: readonly number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const k = Math.floor(s.length / 2);
  return s.length % 2 ? s[k]! : (s[k - 1]! + s[k]!) / 2;
}

/** До сотни рублей: «обычно 22 400 ₽», а не «22 386,50 ₽». */
const toHundred = (kop: number): Money => (Math.round(kop / 10_000) * 10_000) as Money;

export function usualSpending<T extends AnalyticsTx>(input: UsualInput<T>): Usual {
  const candidates = fullMonths(input.today, input.trackingStartedAt);
  const life = input.txs.filter((t) => {
    const k = t.link?.kind;
    if (k === 'obligation' || k === 'goal' || k === 'loan') return false;
    return !(t.type === 'expense' && !t.link && input.isPayment?.(t));
  });
  const reports = candidates.map((m) => monthReport(life, m)).filter((r) => r.count >= USUAL_MIN_RECORDS);
  const months = reports.map((r) => r.month);

  const part = (key: 'need' | 'want'): UsualPart => {
    const est = input.estimate[key];
    if (input.pinned?.[key] && isKnown(est)) return { value: est.value, known: true, source: 'pinned', months: 0 };
    const samples = reports.map((r) => Math.max(0, r[key]));
    if (samples.length === 0) {
      return isKnown(est)
        ? { value: est.value, known: true, source: 'estimate', months: 0 }
        : { value: ZERO, known: false, source: 'none', months: 0 };
    }
    const row = isKnown(est) ? [est.value, ...samples] : samples;
    return { value: toHundred(middle(row)), known: true, source: 'history', months: samples.length };
  };
  return { need: part('need'), want: part('want'), months };
}
